import {
  type AccentId,
  type CandidateBehavior,
  type CandidateProfileId,
  type DurationMinutes,
  type InterviewSetup,
  type MaterialKind,
  type PersonaId,
  type ScenarioId,
  type VoiceEngine,
  type VoiceId,
} from '../shared/contract';

export * from '../shared/contract';

/** `candidate` is always the human in the room; `interviewer` is always the AI voice. */
export interface TranscriptEntry {
  id: string;
  role: 'candidate' | 'interviewer';
  status: 'complete' | 'speaking';
  text: string;
}

export interface Option<T extends string | number> {
  description?: string;
  label: string;
  value: T;
}

export const SCENARIOS: ReadonlyArray<Option<ScenarioId> & { description: string; short: string }> = [
  {
    value: 'roleplay_screening',
    label: 'Screening-call role play',
    short: 'You = recruiter, AI = BDA candidate',
    description: 'You run the 40-minute screening call for the Business Development Associate (Shanghai) role; Tania plays the candidate Tania Min.',
  },
  {
    value: 'tania_followup',
    label: "Tania's follow-up Q&A",
    short: 'Final 15 minutes with the TA Manager',
    description: 'Tania debriefs your call and probes your judgement, TA craft and GTM understanding.',
  },
  {
    value: 'ta_competency',
    label: 'Senior TA competency interview',
    short: 'You = candidate',
    description: 'A competency-based interview for Senior TA Specialist (APAC), with CARE-style follow-ups.',
  },
  {
    value: 'hiring_manager_intake',
    label: 'Hiring-manager intake role play',
    short: 'You = recruiter, AI = sales leader',
    description: 'Practise an intake call with a sales leader opening a GTM role in APAC.',
  },
  {
    value: 'custom',
    label: 'Custom interview',
    short: 'Your role, focus and materials',
    description: 'A general interview built from the role, focus and documents you provide.',
  },
];

export const PERSONAS: ReadonlyArray<{
  description: string;
  id: PersonaId;
  monogram: string;
  name: string;
}> = [
  { description: 'Tough and direct. Pushes hard, little praise.', id: 'harsh', monogram: 'HA', name: 'Harsh / 严厉' },
  { description: 'Calm and fair. A steady, balanced pace.', id: 'gentle', monogram: 'GE', name: 'Gentle / 温和' },
  { description: 'Encouraging. Helps you find your footing.', id: 'supportive', monogram: 'SU', name: 'Supportive / 鼓励' },
];

export const BEHAVIORS: ReadonlyArray<Option<CandidateBehavior>> = [
  { value: 'cooperative', label: 'Cooperative', description: 'Open and forthcoming.' },
  { value: 'surface_level', label: 'Surface-level', description: 'Polished but vague until probed.' },
  { value: 'evasive', label: 'Evasive', description: 'Dodges numbers and specifics.' },
  { value: 'challenging', label: 'Challenging', description: 'Pushes back and tests you.' },
];

export const CANDIDATE_PROFILE_OPTIONS: ReadonlyArray<Option<CandidateProfileId>> = [
  { value: 'official_tania_min', label: 'Tania Min — official CV (default)' },
  { value: 'solid_sdr', label: 'Solid SDR' },
  { value: 'inflated_numbers', label: 'Inflated numbers' },
  { value: 'career_switcher', label: 'Career switcher' },
  { value: 'from_materials', label: 'From my uploaded CV' },
];

export const DIFFICULTY_OPTIONS: ReadonlyArray<Option<InterviewSetup['difficulty']>> = [
  { label: 'Warm-up', value: 'warm_up' },
  { label: 'Standard', value: 'standard' },
  { label: 'Stretch', value: 'stretch' },
];

export const VOICE_LABELS: Record<VoiceId, string> = {
  marin: 'Marin (female, warm)',
  coral: 'Coral (female, bright)',
  sage: 'Sage (female, calm)',
  shimmer: 'Shimmer (female, clear)',
};

/** Female voices GPT-Live documents; the other voices are Realtime-only. */
export const LIVE_VOICES: ReadonlyArray<VoiceId> = ['marin'];

export const ACCENT_OPTIONS: ReadonlyArray<Option<AccentId>> = [
  { value: 'ukrainian', label: 'Ukrainian English (realistic — she’s from Kyiv, lives in Madrid)' },
  { value: 'spanish', label: 'Spanish-influenced English' },
  { value: 'neutral', label: 'Neutral international' },
];

export const VOICE_ENGINE_OPTIONS: ReadonlyArray<Option<VoiceEngine>> = [
  { value: 'gpt-live-1', label: 'GPT-Live 1 (newest)' },
  { value: 'gpt-realtime-2.1', label: 'GPT-Realtime 2.1' },
];

export const MATERIAL_KIND_LABELS: Record<MaterialKind, string> = {
  booklet: 'Booklet',
  job_brief: 'Job brief',
  candidate_cv: 'Candidate CV',
  cbi_guide: 'CBI guide',
  company: 'Company',
  my_cv: 'My CV',
  other: 'Other',
};

/** Kinds a user can choose when uploading. */
export const UPLOAD_KINDS: ReadonlyArray<MaterialKind> = [
  'job_brief',
  'candidate_cv',
  'cbi_guide',
  'company',
  'my_cv',
  'other',
];

export const DEFAULT_SETUP: InterviewSetup = {
  scenario: 'roleplay_screening',
  persona: 'gentle',
  candidateBehavior: 'surface_level',
  candidateProfile: 'official_tania_min',
  difficulty: 'standard',
  durationMinutes: 60 satisfies DurationMinutes,
  focus: '',
  language: 'en',
  accent: 'ukrainian',
  role: 'Senior Talent Acquisition Specialist (China, remote · APAC GTM) — Ideals',
  voice: 'marin',
  voiceEngine: 'gpt-live-1',
  useDefaultMaterials: true,
  materials: [],
};

export function isRolePlay(scenario: ScenarioId): boolean {
  return scenario === 'roleplay_screening';
}
